import { APP_INITIALIZER, ErrorHandler, Injectable, Provider, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { constants } from '@shared/constants/app-constants';

import { getEnvironment } from '../app.config';
// Imported by relative path, not through the @shared/services barrel: this file is part of that
// barrel, and the cycle would leave the symbol undefined at decoration time.
import { ChunkLoadRecoveryService, ChunkRecoveryStage, isChunkLoadFailure } from '../chunk-load-recovery/chunk-load-recovery.service';

export const initializeSentry = (release: string) => {
  const isLocalhost = location.hostname === 'localhost';

  Sentry.init({
    dsn: getEnvironment().production ? constants.SENTRY_PROD_DSN : constants.SENTRY_DSN,
    environment: getEnvironment().name,
    release,
    attachStacktrace: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: !isLocalhost,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.5,
  });
};

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_NUMERIC_SEGMENT = /^\d{6,}$/;

/**
 * Collapses the volatile parts of a route so recovery events group into one issue instead of one
 * per customer or workflow id, and so those ids stay out of Sentry's indexed fields.
 */
export const normalizeRoute = (url: string): string => {
  const path = (url || '').split(/[#?]/)[0].replace(/^[a-z]+:\/\/[^/]+/i, '');

  return path
    .split('/')
    .map((segment) => (UUID_SEGMENT.test(segment) || LONG_NUMERIC_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
};

/**
 * Records what chunk-load recovery did. One fixed fingerprint so it stays a single issue, which is
 * what makes "the old chunk-load issues went quiet" measurable rather than merely absent.
 */
export const captureChunkLoadRecovery = (stage: ChunkRecoveryStage, url: string) => {
  Sentry.captureMessage(`Chunk load recovery: ${stage}`, {
    level: stage === 'gave-up' ? 'error' : 'info',
    fingerprint: ['chunk-load-recovery'],
    tags: { chunkRecoveryStage: stage, route: normalizeRoute(url) },
  });
};

/**
 * Wraps Sentry's ErrorHandler so a failed build-asset import is recovered instead of merely
 * reported. Every other error is delegated untouched.
 */
@Injectable()
export class ChunkAwareErrorHandler implements ErrorHandler {
  private readonly delegate = Sentry.createErrorHandler({ showDialog: false });
  private readonly recovery = inject(ChunkLoadRecoveryService);
  private readonly router = inject(Router);

  public handleError(error: unknown): void {
    if (!isChunkLoadFailure(error)) {
      this.delegate.handleError(error);

      return;
    }

    // canRetry: false - this error did not come from a navigation, so re-running the current
    // route would not re-attempt the import that failed. Go straight to the reload.
    const stage = this.recovery.recover(this.router.url, { canRetry: false });

    captureChunkLoadRecovery(stage, this.router.url);

    // A reload is about to discard this document and a retry is expected to succeed - neither is a
    // fault worth reporting as unhandled. Only exhaustion reaches the normal handler.
    if (stage === 'gave-up') {
      this.delegate.handleError(error);
    }
  }
}

/* Sentry providers configuration */
export const sentryProviders: () => Provider[] = () => {
  return [
    {
      provide: ErrorHandler,
      useClass: ChunkAwareErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => ({}),
      deps: [Sentry.TraceService],
      multi: true,
    },
  ];
};
